// C:\VUE3\platform\src\store\mainStore.js
import { defineStore } from 'pinia';

import { ref, computed } from 'vue';
import userUtils from '@/common/userUtils';
import loginApi from '@/common/api/loginApi';
import { logError, logWarn, logInfo, logDebug } from '@/utils/logHelper';

// 표준 목록(간단 화이트리스트)
const LANGS = ['ko','en','ja'];
const COUNTRIES = ['KR','US','JP'];
const CURRENCIES = ['KRW','USD','JPY','EUR','GBP','CAD','AUD'];
const DEFAULTS = { country: 'KR', language: 'ko', currency: 'KRW', locale: 'ko-KR' };

const COUNTRY_TO_LANG = { KR:'ko', US:'en', JP:'ja' };
const COUNTRY_TO_CURR = { KR:'KRW', US:'USD', JP:'JPY' };
const LANG_TO_LOCALE   = { ko:'ko-KR', en:'en-US', ja:'ja-JP' };

function normLang(l){ return LANGS.includes(l) ? l : DEFAULTS.language; }
function normCountry(c){ return COUNTRIES.includes(c) ? c : DEFAULTS.country; }
function normCurrency(c){ return CURRENCIES.includes(c) ? c : DEFAULTS.currency; }
function toLocale(lang){ return LANG_TO_LOCALE[lang] || DEFAULTS.locale; }

export const useMainStore = defineStore('mainStore', () => {

  const isLoading = ref(localStorage.getItem('onTx') === 'true');
  const isLogin = ref(!!userUtils.getAccessToken());
  const user = ref(userUtils.getUserInfo());

  // ✅ 기본값 KR/ko로 통일
  const country  = ref(normCountry(localStorage.getItem('countryCode')   || DEFAULTS.country));
  const language = ref(normLang   (localStorage.getItem('languageCode')  || DEFAULTS.language));
  const currency = ref(normCurrency(localStorage.getItem('currencyCode') || DEFAULTS.currency));
  // locale은 Intl 전용 보조값: 언어에서 파생(직접 저장값이 있으면 우선)
  const locale = ref(localStorage.getItem('locale') || toLocale(language.value));

  // 부팅 완료 후 감지 허용 플래그
  const localeReady = ref(false);

  function SET_LOADING(loadingFlag) {
    isLoading.value = !!loadingFlag;
    localStorage.setItem('onTx', isLoading.value);
  }
  function SET_IS_LOGIN(b) { isLogin.value = !!b; }

  function SET_COUNTRY(countryCode) {

    country.value = normCountry(countryCode);
    localStorage.setItem('countryCode', country.value);
  }
  function SET_CURRENCY(currencyCode) {

    currency.value = normCurrency(currencyCode);
    localStorage.setItem('currencyCode', currency.value);
  }
  function SET_LANGUAGE(languageCode) {

    language.value = normLang(languageCode);
    localStorage.setItem('languageCode', language.value);
    // 언어가 바뀌면 Intl 로케일도 합리적으로 따라가도록(수동 오버라이드가 있으면 이후 SET_LOCALE로 덮을 수 있음)
    if (!localStorage.getItem('locale')) {
      locale.value = toLocale(language.value);
    }
  }
  function SET_LOCALE(localeValue) {

    // 최소 형태 검사 (xx-YY)
    locale.value = typeof localeValue === 'string' && localeValue.includes('-')
      ? localeValue
      : toLocale(language.value);
    localStorage.setItem('locale', locale.value);
  }

  function SET_USER(userInfo) {
    user.value = userInfo || null;
    if (userInfo) userUtils.setUserInfo(userInfo);
    else userUtils.clearUserInfo();
  }
  function REFRESH_USER_FROM_SESSION() { user.value = userUtils.getUserInfo(); }

  async function onLoginAction(serverResponse) {
    userUtils.setLoginResponse(serverResponse);
    SET_IS_LOGIN(true);
    SET_USER(userUtils.getUserInfo());
  }

  async function onLogoutAction() {
    try {
      SET_IS_LOGIN(false);
      SET_LOADING(false);
      SET_USER(null);
      sessionStorage.removeItem('quoteData');
      sessionStorage.clear();
    } catch (error) {
      logError('로그아웃 중 오류 발생:', error);
    }
  }

  async function checkAuthAction() {
    const token = userUtils.getAccessToken();
    logDebug('[checkAuthAction] token:', token);
    if (!token) { SET_IS_LOGIN(false); SET_USER(null); return; }
    try {
      await loginApi.verifyToken({ token });
      SET_IS_LOGIN(true);
      REFRESH_USER_FROM_SESSION();
    } catch {
      SET_IS_LOGIN(false);
      SET_USER(null);
    }
  }

  function MARK_LOCALE_READY() { localeReady.value = true; }

  return {
    // state
    isLoading, isLogin, user,
    country, currency, language, locale,
    localeReady,

    // actions
    SET_LOADING, SET_IS_LOGIN,
    SET_USER, REFRESH_USER_FROM_SESSION,
    SET_COUNTRY, SET_CURRENCY, SET_LANGUAGE, SET_LOCALE,
    onLoginAction, onLogoutAction, checkAuthAction,
    MARK_LOCALE_READY,
  };
});
